import React, { DOMAttributes, useCallback } from 'react'
import styled from 'styled-components'
import { HiddenSelect, mergeProps, useOverlayTrigger, useSelect, Key } from 'react-aria'
import { Item, useSelectState } from 'react-stately'
import * as R from 'remeda'
import { UnstyledButton } from '../../../../core-components/unstyled-button/UnstyledButton'
import { ScreenReaderOnly, Text } from '../../../../core-components'
import { YearMonthSelectPopover } from './YearMonthSelectPopover'
import { YearMonthSelectList } from './YearMonthSelectList'

type Props = {
  'data-test-id'?: string
  isOpen?: boolean
  label: string
  onChange?: (value: string) => void
  onKeyDown?: DOMAttributes<HTMLButtonElement>['onKeyDown']
  onOpenChange?: (isOpen: boolean) => void
  options: string[]
  value: string
}

export const YearMonthSelect = ({
  value,
  options,
  label,
  onChange,
  isOpen,
  onOpenChange,
  'data-test-id': dataTestId,
  onKeyDown,
}: Props) => {
  const ariaProps = {
    label,
    children: ({ value }: { value: string }) => <Item key={value}>{value}</Item>,
    items: options.map(option => ({ value: option })),
    selectedKey: value,
    onSelectionChange: useCallback((key: Key | null) => onChange?.(String(key)), [onChange]),
    isOpen,
    onOpenChange,
    onKeyDown,
  }
  const state = useSelectState(ariaProps)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const { triggerProps, valueProps, menuProps, labelProps } = useSelect(
    ariaProps,
    state,
    triggerRef
  )
  const { triggerProps: overlayTriggerProps, overlayProps } = useOverlayTrigger(
    { type: 'listbox' },
    {
      ...state,
      setOpen: _ => _,
      open: R.doNothing(),
      toggle: R.doNothing(),
      close: R.doNothing(), // by default overlay closes on scroll https://github.com/adobe/react-spectrum/issues/1852#issuecomment-873560234
    },
    triggerRef
  )
  return (
    <>
      <HiddenSelect state={state} triggerRef={triggerRef} />
      <ScreenReaderOnly {...labelProps}>{label}</ScreenReaderOnly>
      <StyledButton
        h={7}
        ariaProps={{
          ...mergeProps(triggerProps, overlayTriggerProps),
          elementRef: triggerRef,
        }}
        data-test-id={dataTestId}
      >
        <Text {...valueProps} textStyle='form-value-emphasis' color='fg/major'>
          {value}
        </Text>
      </StyledButton>
      {state.isOpen && (
        <YearMonthSelectPopover onClose={state.close} {...overlayProps}>
          <YearMonthSelectList state={state} {...menuProps} />
        </YearMonthSelectPopover>
      )}
    </>
  )
}

const StyledButton = styled(UnstyledButton)`
  border-radius: ${({ theme }) => theme.radii[10]};
  padding: 0 ${({ theme }) => theme.space[1]};
  transition: padding 100ms ease-in-out;

  :hover {
    padding: 0 ${({ theme }) => theme.space[3]};
    background: ${({ theme }) => theme.colors['bg/form-item-hovered']};
  }

  :focus-visible {
    outline: none;
    padding: 0 ${({ theme }) => theme.space[3]};
    background: ${({ theme }) => theme.colors['bg/form-item-hovered']};
    box-shadow: ${({ theme }) => theme.shadows['border/focus-indicator']};
  }
`
